import axios from 'axios';
import { api } from '../utils/api';

// API base URL
const API_URL = 'http://localhost:5004/api';

// Professor interface
export interface Professor {
  id: number;
  name: string;
  title: string | null;
  email: string;
  department: string | null;
  university: string | null;
  research_summary: string | null;
  research_areas: string[];
  webpage: string | null;
  response_score: number;
  last_contacted: string | null;
}

// Define pagination interface
export interface Pagination {
  total: number;
  pages: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;
}

// Define response interface
export interface ProfessorsResponse {
  professors: Professor[];
  pagination: Pagination;
}

// Filter parameters interface
export interface ProfessorFilters {
  searchQuery?: string;
  university?: string;
  department?: string;
  researchArea?: string;
}

// Get all professors
export const getProfessors = async (
  page: number = 1, 
  perPage: number = 20,
  filters?: ProfessorFilters
): Promise<ProfessorsResponse> => {
  try {
    // Build query parameters
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());
    params.append('t', new Date().getTime().toString()); // cache-busting
    
    // Add filters if provided
    if (filters?.searchQuery) {
      params.append('q', filters.searchQuery);
    }
    if (filters?.university) {
      params.append('university', filters.university);
    }
    if (filters?.department) {
      params.append('department', filters.department);
    }
    if (filters?.researchArea) {
      params.append('research_area', filters.researchArea);
    }
    
    console.log(`Fetching professors with filters:`, { page, perPage, filters });
    const response = await api.get(`/professors?${params.toString()}`);
    console.log('API Response:', response.data);
    
    // Check which format the response is in
    if (response.data.items && response.data.pagination) {
      // New format with pagination
      console.log(`Found ${response.data.items.length} professors with pagination. Total: ${response.data.pagination.total}`);
      return {
        professors: response.data.items,
        pagination: response.data.pagination
      };
    } else if (response.data.professors) {
      // Old format without pagination
      return {
        professors: response.data.professors,
        pagination: {
          total: response.data.professors.length,
          pages: 1,
          page: 1,
          per_page: response.data.professors.length,
          has_next: false,
          has_prev: false
        }
      };
    } else {
      // Fallback to mock data if response format is unexpected
      console.warn('Unexpected response format, using mock data');
      return {
        professors: mockProfessors,
        pagination: {
          total: mockProfessors.length,
          pages: 1,
          page: 1,
          per_page: mockProfessors.length,
          has_next: false,
          has_prev: false
        }
      };
    }
  } catch (error) {
    console.error('Error fetching professors:', error);
    // Fallback to mock data on error
    return {
      professors: mockProfessors,
      pagination: {
        total: mockProfessors.length,
        pages: 1,
        page: 1,
        per_page: mockProfessors.length,
        has_next: false,
        has_prev: false
      }
    };
  }
};

// Get professor by ID
export const getProfessorById = async (id: number): Promise<Professor> => {
  try {
    const response = await api.get(`/professors/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching professor with ID ${id}:`, error);
    throw error;
  }
};

// Get all universities
export const getUniversities = async (): Promise<any[]> => {
  try {
    const response = await api.get(`/universities`);
    // Transform array of strings to array of objects with id and name
    return response.data.universities.map((name: string, index: number) => ({
      id: index + 1,
      name: name
    }));
  } catch (error) {
    console.error('Error fetching universities:', error);
    throw error;
  }
};

// Get all departments (optionally filtered by university)
export const getDepartments = async (university?: string): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (university) {
      params.append('university', university);
    }
    const url = `/departments${params.toString() ? '?' + params.toString() : ''}`;
    const response = await api.get(url);
    // Transform array of strings to array of objects with id and name
    return response.data.departments.map((name: string, index: number) => ({
      id: index + 1,
      name: name,
      university_id: 0 // Default value since we don't have university_id in the response
    }));
  } catch (error) {
    console.error('Error fetching departments:', error);
    throw error;
  }
};

// Get all research areas (optionally filtered by university)
export const getResearchAreas = async (university?: string): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (university) {
      params.append('university', university);
    }
    const url = `/research-areas${params.toString() ? '?' + params.toString() : ''}`;
    const response = await api.get(url);
    // Transform array of strings to array of objects with id and name
    return response.data.research_areas.map((name: string, index: number) => ({
      id: index + 1,
      name: name
    }));
  } catch (error) {
    console.error('Error fetching research areas:', error);
    throw error;
  }
};

// Mock data for testing when API is not available
export const mockProfessors: Professor[] = [
  {
    id: 999,
    name: 'Dr. Ashok Kumar',
    title: 'Associate Professor',
    email: 'ks.ashok@gmail.com',
    department: 'Computer Science',
    university: 'Stanford University',
    research_summary: 'My research focuses on developing advanced AI systems that can understand and generate human language. I work on machine learning models for natural language processing, with applications in education, healthcare, and human-computer interaction.',
    research_areas: ['Artificial Intelligence', 'Machine Learning', 'Natural Language Processing'],
    webpage: 'https://cs.stanford.edu/~ashok',
    response_score: 0.9,
    last_contacted: null
  },
  {
    id: 1,
    name: 'Dr. Jane Smith',
    title: 'Associate Professor',
    email: 'jane.smith@stanford.edu',
    department: 'Computer Science',
    university: 'Stanford University',
    research_summary: 'Working on AI and machine learning applications',
    research_areas: ['Artificial Intelligence', 'Machine Learning'],
    webpage: 'https://www.stanford.edu/faculty/jsmith',
    response_score: 0.8,
    last_contacted: null
  },
  {
    id: 2,
    name: 'Dr. John Doe',
    title: 'Professor',
    email: 'john.doe@mit.edu',
    department: 'Electrical Engineering',
    university: 'MIT',
    research_summary: 'Researching advanced robotics systems',
    research_areas: ['Robotics', 'Control Systems'],
    webpage: 'https://www.mit.edu/faculty/jdoe',
    response_score: 0.6,
    last_contacted: null
  },
  {
    id: 3,
    name: 'Dr. Alice Johnson',
    title: 'Assistant Professor',
    email: 'alice.johnson@cmu.edu',
    department: 'Computer Science',
    university: 'Carnegie Mellon',
    research_summary: 'Developing new machine learning algorithms',
    research_areas: ['Machine Learning', 'Data Mining'],
    webpage: 'https://www.cmu.edu/faculty/ajohnson',
    response_score: 0.9,
    last_contacted: null
  }
];
